import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Alert, Spinner, Row, Col, Card } from 'react-bootstrap';
import api from "../../../api/axiosConfig";
import { useAuth } from '../../../context/AuthContext';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import './EditPost.css';

const EditPost = () => {
    const { postId } = useParams();
    const navigate = useNavigate();
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('');
    const [featured, setFeatured] = useState(false);
    const [eventDate, setEventDate] = useState('');
    const [status, setStatus] = useState(0);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const [post, setPost] = useState('');
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentImages, setCurrentImages] = useState([]);
    const [captions, setCaptions] = useState({});

    useEffect(() => {
        const fetchPost = async () => {
            try {
                const siteId = user?.site_id;
                if (!siteId) {
                    setError('Site ID not found. Please contact support.');
                    setLoading(false);
                    return;
                }

                setLoading(true);
                const response = await api.get(`/posts/${postId}?site_id=${siteId}`);
                console.log("Post API response:", response.data);
                if (response.data.success) {
                    const postData = response.data.data;
                    setTitle(postData.title);
                    setPost(postData.post);
                    setCategory(postData.category || '');
                    setFeatured(postData.featured === 1);
                    setEventDate(postData.event_date || '');
                    setStatus(postData.status);

                    // Fetch all post images
                    await fetchPostImages(postData.id, siteId);
                } else {
                    setError(response.data.message || 'Failed to fetch post.');
                }

            } catch (err) {
                setError('Failed to fetch post.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        const fetchPostImages = async (postId, siteId) => {
            try {
                const pictureResponse = await api.get(`/pictures?site_id=${siteId}`);

                if (pictureResponse.data.success && pictureResponse.data.data.length > 0) {
                    // Filter images by post_id on the frontend
                    const pictures = pictureResponse.data.data.filter(picture =>
                        picture.post_id == postId || picture.post_id === postId
                    );

                    console.log(`Total images for site: ${pictureResponse.data.data.length}`);
                    console.log(`Filtered images for post ${postId}: ${pictures.length}`);

                    setCurrentImages(pictures);

                    const initialCaptions = {};
                    pictures.forEach(picture => {
                        initialCaptions[picture.id] = picture.caption || '';
                    });
                    setCaptions(initialCaptions);
                } else {
                    setCurrentImages([]);
                }
            } catch (err) {
                console.error('Failed to fetch post images:', err);
                setCurrentImages([]);
            }
        }; // Closing brace for fetchPostImages

        fetchPost(); // Calling fetchPost inside useEffect
    }, [postId, user?.site_id]); // Dependency array for useEffect

    // Handle multiple file selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Validate files
        const validFiles = [];
        const errors = [];

        files.forEach(file => {
            // Validate file type
            if (!file.type.match('image/jpeg') && !file.type.match('image/png')) {
                errors.push(`${file.name}: Please select valid JPG or PNG files only`);
                return;
            }

            // Validate file size (max 2MB)
            if (file.size > 2 * 1024 * 1024) {
                errors.push(`${file.name}: Image size should be less than 2MB`);
                return;
            }

            validFiles.push(file);
        });

        if (errors.length > 0) {
            setError(errors.join(', '));
            return;
        }

        if (validFiles.length > 0) {
            setSelectedFiles(prev => [...prev, ...validFiles]);
            setError('');

            // Create previews for new files
            validFiles.forEach(file => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    setImagePreviews(prev => [...prev, {
                        id: `new-${Date.now()}-${file.name}`,
                        url: e.target.result,
                        file: file,
                        isNew: true
                    }]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    // Upload multiple images to server
    const uploadImages = async (files) => {
        const uploadPromises = files.map(async (file) => {
            const formData = new FormData();
            formData.append('picture_files', file);
            formData.append('caption', captions[`new-${file.name}`] || '');
            formData.append('post_id', postId);

            try {
                const response = await api.post('/pictures', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.data.success) {
                    return response.data.data;
                } else {
                    throw new Error(response.data.message || 'Failed to upload image');
                }
            } catch (err) {
                console.error('Image upload error:', err.response?.data || err.message);
                throw new Error(err.response?.data?.message || 'Failed to upload image');
            }
        });

        return Promise.all(uploadPromises);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccessMessage('');
        setUploadProgress(0);

        try {
            const siteId = user?.site_id;
            if (!siteId) {
                setError('Site ID not found. Please contact support.');
                setLoading(false);
                return;
            }

            // Upload new images if selected
            if (selectedFiles.length > 0) {
                try {
                    await uploadImages(selectedFiles);
                } catch (uploadError) {
                    console.warn('Some images failed to upload:', uploadError.message);
                    // Continue with post update even if some image uploads fail
                }
            }

            // Update captions for existing images
            const captionUpdatePromises = currentImages.map(image => {
                if (captions[image.id] !== undefined) {
                    return api.put(`/pictures/${image.id}`, {
                        caption: captions[image.id],
                        updated_by: user.id
                    });
                }
                return Promise.resolve();
            });

            await Promise.all(captionUpdatePromises);

            // Update the post
            const response = await api.put(`/posts/${postId}`, {
                site_id: siteId,
                title: title,
                post: post,
                category: category,
                featured: featured ? 1 : 0,
                event_date: eventDate,
                status: status,
                updated_by: user.id,
            });

            if (response.data.success) {
                setSuccessMessage('Post updated successfully!');

                // Navigate after a short delay to show success message
                setTimeout(() => {
                    navigate('/site-admin/posts');
                }, 2000);
            } else {
                setError(response.data.message || 'An error occurred while updating the post.');
            }
        } catch (err) {
            console.error('Update post error:', err.response?.data || err.message);
            setError(err.response?.data?.message || 'An error occurred while updating the post.');
        } finally {
            setLoading(false);
        }
    };

    const handleEditorChange = (event, editor) => {
        const data = editor.getData();
        setPost(data);
    };

    // Remove selected image from new files
    const removeNewImage = (previewId) => {
        setImagePreviews(prev => prev.filter(preview => preview.id !== previewId));
        setSelectedFiles(prev => prev.filter(file =>
            `new-${Date.now()}-${file.name}` !== previewId
        ));
    };

    // Delete current image from server
    const deleteCurrentImage = async (imageId) => {
        try {
            const response = await api.delete(`/pictures/${imageId}`);
            if (response.data.success) {
                setCurrentImages(prev => prev.filter(img => img.id !== imageId));
                setSuccessMessage('Image deleted successfully');

                // Remove caption from state
                setCaptions(prev => {
                    const newCaptions = { ...prev };
                    delete newCaptions[imageId];
                    return newCaptions;
                });
            }
        } catch (err) {
            console.error('Failed to delete image:', err);
            setError('Failed to delete image');
        }
    };

    // Handle caption change
    const handleCaptionChange = (imageId, caption) => {
        setCaptions(prev => ({
            ...prev,
            [imageId]: caption
        }));
    };

    return (
        <Container className="edit-post-container">
            <div className="edit-post-header">
                <h1>Edit Post</h1>
            </div>

            {error && <Alert className="alert-edit alert-danger-edit">{error}</Alert>}
            {successMessage && <Alert className="alert-edit alert-success-edit">{successMessage}</Alert>}

            <Form onSubmit={handleSubmit} className="edit-post-form">
                <Row>
                    <Col md={8}>
                        <div className="form-section">
                            <h3 className="form-section-title">Post Information</h3>

                            <div className="form-group-edit">
                                <label className="form-label-edit">Title *</label>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    required
                                    className="form-control-edit"
                                />
                            </div>

                            <div className="form-group-edit">
                                <label className="form-label-edit">Post Content</label>
                                <div className="ckeditor-edit-container">
                                    <CKEditor
                                        editor={ClassicEditor}
                                        data={post}
                                        onChange={handleEditorChange}
                                        config={{
                                            toolbar: ['heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote', 'insertTable', 'undo', 'redo']
                                        }}
                                    />
                                </div>
                            </div>

                            <Row>
                                <Col md={6}>
                                    <div className="form-group-edit">
                                        <label className="form-label-edit">Category</label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter category"
                                            value={category}
                                            onChange={(e) => setCategory(e.target.value)}
                                            className="form-control-edit"
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="form-group-edit">
                                        <label className="form-label-edit">Event Date</label>
                                        <Form.Control
                                            type="datetime-local"
                                            value={eventDate}
                                            onChange={(e) => setEventDate(e.target.value)}
                                            className="form-control-edit"
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="status-container">
                                        <label className="form-label-custom">Status</label>
                                        <Form.Control
                                            as="select"
                                            value={status}
                                            onChange={(e) => setStatus(parseInt(e.target.value))}
                                            className="form-control-custom"
                                        >
                                            <option value={0}>Draft</option>
                                            <option value={1}>Published</option>
                                        </Form.Control>
                                        <span className={`status-indicator ${status === 1 ? 'status-published' : 'status-draft'}`}>
                                            {status === 1 ? 'Published' : 'Draft'}
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="featured-container">
                                        <div className="featured-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={featured}
                                                onChange={(e) => setFeatured(e.target.checked)}
                                            />
                                            <span className="featured-label">Featured Post</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Col>

                    <Col md={4}>
                        <Card className="featured-image-edit-card">
                            <Card.Header className="card-header-edit">
                                <h5 className="mb-0">Post Images</h5>
                            </Card.Header>
                            <Card.Body className="card-body-edit">
                                <div className="form-group-edit">
                                    <label className="form-label-edit">Upload New Images</label>
                                    <div className="file-input-edit">
                                        <Form.Control
                                            type="file"
                                            accept="image/jpeg, image/png"
                                            onChange={handleFileSelect}
                                            multiple
                                        />
                                    </div>
                                    <Form.Text className="text-muted">
                                        Supported formats: JPG, PNG only. Max size: 2MB per file
                                    </Form.Text>
                                </div>

                                {uploadProgress > 0 && uploadProgress < 100 && (
                                    <div className="upload-progress-edit">
                                        <div className="progress-info-edit">
                                            <small>Uploading...</small>
                                            <small>{uploadProgress}%</small>
                                        </div>
                                        <div className="progress-bar-edit">
                                            <div
                                                className="progress-bar-fill-edit"
                                                style={{ width: `${uploadProgress}%` }}
                                            ></div>
                                        </div>
                                    </div>
                                )}

                                {/* Current Images */}
                                {currentImages.length > 0 && (
                                    <div className="current-images-section">
                                        <h6 className="section-subtitle">Current Images</h6>
                                        {currentImages.map(image => (
                                            <div key={image.id} className="image-item">
                                                <div className="image-preview">
                                                    <img
                                                        src={image.picture_path}
                                                        alt={`Current ${image.id}`}
                                                    />
                                                    <div className="image-actions-post">
                                                        <button
                                                            className="btn-delete-current-post"
                                                            onClick={() => deleteCurrentImage(image.id)}
                                                            title="Delete image"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                    <div className="image-badge-post">
                                                        CURRENT
                                                    </div>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Image caption"
                                                    value={captions[image.id] || ''}
                                                    onChange={(e) => handleCaptionChange(image.id, e.target.value)}
                                                    className="form-control-edit mt-2"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* New Image Previews */}
                                {imagePreviews.filter(preview => preview.isNew).length > 0 && (
                                    <div className="new-images-section">
                                        <h6 className="section-subtitle">New Images</h6>
                                        {imagePreviews.filter(preview => preview.isNew).map(preview => (
                                            <div key={preview.id} className="image-item">
                                                <div className="image-preview">
                                                    <img
                                                        src={preview.url}
                                                        alt="New preview"
                                                    />
                                                    <div className="image-actions-post">
                                                        <button
                                                            className="btn-remove-new-post"
                                                            onClick={() => removeNewImage(preview.id)}
                                                            title="Remove new image"
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                    <div className="image-badge-post">
                                                        NEW
                                                    </div>
                                                </div>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Image caption"
                                                    value={captions[preview.id] || ''}
                                                    onChange={(e) => handleCaptionChange(preview.id, e.target.value)}
                                                    className="form-control-edit mt-2"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <div className="form-actions-edit">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={loading}
                        className="btn-primary-edit"
                    >
                        {loading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Updating...
                            </>
                        ) : (
                            'Update Post'
                        )}
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => navigate('/site-admin/posts')}
                        className="btn-secondary-edit"
                    >
                        Cancel
                    </Button>
                </div>
            </Form>
        </Container>
    );
};

export default EditPost;